import type { SnapshotBehaviourId, SnapshotDrillId } from "@healthos/shared";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { ArrowRight, ArrowsHorizontal, CaretDown, CaretUp } from "@phosphor-icons/react";
import { cn } from "../lib/cn";
import { EyebrowBullet } from "../primitives/eyebrow-bullet";
import { BehaviourIcon } from "./snapshot/behaviour-icon";
import { BehaviourSpark } from "./snapshot/behaviour-spark";
import { renderInlineMarkdown } from "./snapshot/inline-markdown";
import { SNAPSHOT_BEHAVIOURS } from "./snapshot/snapshot-tokens";

/** The Snapshot digest on `/today`: */

export type SnapshotDigestTone = "calm" | "brand" | "warm" | "quiet";

export type SnapshotDigestTrend = "up" | "flat" | "down";

export type SnapshotStanding =
  | "strong"
  | "steady"
  | "holding"
  | "lifting"
  | "watch"
  | "quiet"
  | "gathering";

export interface SnapshotDigestRow {
  behaviour: SnapshotBehaviourId;
  label: string;
  /** One plain line for the week. */
  line: string;
  /** Where it stands, in the shared vocabulary: Lifting, Holding, Watch. */
  status: string;
  /** The canonical word behind it, which the chip spells out on hover. */
  standing: SnapshotStanding;
  /** Which way its own series is going, which picks the mark. */
  trend: SnapshotDigestTrend;
  tone: SnapshotDigestTone;
  /** The shape it has been making, oldest first. Null is a day with no reading. */
  spark: ReadonlyArray<number | null>;
  /** Nothing has reported yet, so the row is waiting rather than reading. */
  gathering: boolean;
  /** The level this behaviour is practising, when it has one. */
  level: number | null;
  /** True when the member's current goal is on this behaviour. */
  goal: boolean;
  /** The readings behind it, when there is a tab to open. */
  drill: SnapshotDrillId | null;
}

export interface SnapshotDigestProps {
  /** The week in one sentence, from the member's own readings. */
  headline: string;
  /** What the card wants the member to take from it, when there is something. */
  say?: string | null;
  rows: ReadonlyArray<SnapshotDigestRow>;
  /** The behaviours the member's own scores and goal are pointing at. */
  focus?: ReadonlyArray<string>;
  /** The span the lanes cover, stated once rather than left to a hover. */
  window?: string;
  onOpen: () => void;
  /** Opens one behaviour's readings. Rows are inert without it. */
  onOpenRow?: (drill: SnapshotDrillId) => void;
  className?: string;
}

/** Where a behaviour stands, as a mark rather than a word. */
/** Where the behaviour stands, as the chip the rest of the system writes state in rather than a column of loose uppercase prose. */
const TREND_ICON: Record<SnapshotDigestTrend, PhosphorIcon> = {
  up: CaretUp,
  flat: ArrowsHorizontal,
  down: CaretDown,
};

export function SnapshotDigest({
  headline,
  say = null,
  rows,
  focus = [],
  window = "Last 7 days",
  onOpen,
  onOpenRow,
  className,
}: SnapshotDigestProps) {
  // Every behaviour still waiting is one fact, not five: five rows of
  // "Waiting for..." read as a broken card rather than a patient one.
  const allWaiting =
    rows.length > 0 && rows.every((row) => row.gathering && row.spark.length === 0);
  return (
    <section className={cn("snap-dg", className)} aria-labelledby="snap-dg-head">
      <span aria-hidden="true" className="snap-dg-bloom" />
      <div className="snap-dg-top">
        <EyebrowBullet variant="plain">How we see you now</EyebrowBullet>
      </div>
      <h2 className="snap-dg-headline" id="snap-dg-head">
        {renderInlineMarkdown(headline)}
      </h2>
      {say ? <p className="snap-dg-say">{renderInlineMarkdown(say)}</p> : null}
      {/* Five tiles, one per behaviour, each in its own colour: the week reads
          at a glance as a row of states rather than a column of sentences. The
          sentence for each rides its tile's hover. */}
      <ul className="snap-dg-tiles">
        {rows.map((row) => {
          const meta = SNAPSHOT_BEHAVIOURS.find((b) => b.id === row.behaviour);
          const open =
            row.drill && onOpenRow ? () => onOpenRow(row.drill as SnapshotDrillId) : null;
          const Tile = open ? "button" : "div";
          // Nothing measured has no state and no direction: "Waiting", without a
          // trend mark, rather than "Not measured" beside a sideways arrow.
          const waiting =
            (row.gathering && row.spark.length === 0) ||
            row.standing === "quiet" ||
            row.standing === "gathering";
          const TrendIcon = TREND_ICON[row.trend];
          return (
            <li
              key={row.behaviour}
              className={`snap-dg-tile${waiting ? " is-waiting" : ""}`}
              style={{ ["--accent" as string]: meta?.color }}
            >
              <Tile
                {...(open ? { type: "button" as const, onClick: open } : {})}
                title={row.line}
                aria-label={`${row.label}: ${waiting ? "waiting for a first reading" : row.status}. ${row.line}`}
              >
                <span className="snap-dg-tile-icon" aria-hidden="true">
                  {meta ? <BehaviourIcon name={meta.iconName} size={16} weight="fill" /> : null}
                </span>
                <span className="snap-dg-tile-label">
                  {row.label}
                  {row.goal ? <em className="snap-dg-tag">goal</em> : null}
                </span>
                <span className={`snap-dg-tile-status is-${row.tone}`}>
                  {waiting ? (
                    "Waiting"
                  ) : (
                    <>
                      {row.status}
                      <TrendIcon size={12} weight="bold" aria-hidden="true" />
                    </>
                  )}
                </span>
                {row.level === null ? null : <i className="snap-dg-tile-lv">Lv {row.level}</i>}
                {row.spark.length > 0 ? (
                  <BehaviourSpark
                    values={row.spark}
                    accent={meta?.color}
                    gathering={row.gathering}
                    className="snap-dg-tile-spark"
                  />
                ) : (
                  <span className="snap-dg-tile-spark is-empty" aria-hidden="true" />
                )}
              </Tile>
            </li>
          );
        })}
      </ul>
      {allWaiting ? (
        <p className="snap-dg-waiting-say">
          Readings land here as your devices and logs report in. Connect a wearable, or log
          something, and these start to fill.
        </p>
      ) : null}
      <div className="snap-dg-foot">
        {/* The lanes are a week wide and nothing on the card said so, which is
            answerable from a short line only by guessing. */}
        <span className="snap-dg-focus">
          {window}
          {focus.length > 0 ? ` · Your focus: ${focus.join(", ")}` : ""}
        </span>
        <button type="button" className="snap-dg-cta" onClick={onOpen}>
          View snapshot
          <ArrowRight size={13} weight="bold" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
